import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

interface PreviewItem {
  src: string;
  title?: string;
}

interface PreviewContextValue {
  openPreview: (item: PreviewItem) => void;
}

const PreviewContext = createContext<PreviewContextValue>({ openPreview: () => {} });

export const useImagePreview = () => useContext(PreviewContext);

export function ImagePreviewProvider({ children }: { children: ReactNode }) {
  const [item, setItem] = useState<PreviewItem | null>(null);

  const openPreview = useCallback((next: PreviewItem) => setItem(next), []);
  const close = useCallback(() => setItem(null), []);

  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [item, close]);

  return (
    <PreviewContext.Provider value={{ openPreview }}>
      {children}
      {item && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground-950/80 p-4 animate-fade-in"
          role="dialog"
          aria-modal="true"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-background-50/90 text-foreground-900 transition-colors hover:bg-background-50 md:right-8 md:top-8"
            aria-label="关闭预览"
          >
            <i className="ri-close-line text-2xl" />
          </button>
          <figure
            className="flex max-h-[88vh] max-w-3xl flex-col items-center animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={item.src}
              alt={item.title ? `${item.title} 封面预览` : "图片预览"}
              className="max-h-[80vh] w-auto rounded-lg object-contain"
            />
            {item.title && (
              <figcaption className="mt-4 text-center text-sm text-background-100">
                {item.title}
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </PreviewContext.Provider>
  );
}